import React, { useState, useEffect, useRef } from 'react';
import FlippexLoader from '../components/ui/FlippexLoader';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import AnimatedCounter from '../components/AnimatedCounter';
import ForDevelopersSection from '../components/ForDevelopersSection';
import CTASection from '../components/CTASection';
import RecognitionStrip from '../components/home/RecognitionStrip';
import { 
  ShieldCheck,
  ChartLineUp,
  ArrowRight,
  MapPin,
  Buildings,
  Eye,
  CheckCircle,
  Users,
  FileText,
  Handshake,
  Question,
  Key,
  Percent,
  CurrencyEur,
  House,
  DeviceMobile,
  SquaresFour,
  Coins,
  ChartPieSlice,
  Globe,
  Lock,
  Certificate,
} from '@phosphor-icons/react';
import axios from 'axios';
import { getStatusLabel, getProjectTypeLabel, STATUS_COLORS_SOLID, sortProjects } from '../lib/projectUtils';
import { OpportunityCard } from '../components/marketplace/OpportunityCard';
import { projectTypeMeta } from '../lib/projectTypes';

// Project Type Icon Component (matches ProjectsPage)
const ProjectTypeIcon = ({ type, className = "h-4 w-4" }) => {
  const Icon = projectTypeMeta(type).icon;
  return <Icon className={className} weight="duotone" />;
};

// Animation variants for scroll reveal
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.25, 0.4, 0.25, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: { opacity: 0, y: 60 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.9, ease: [0.25, 0.4, 0.25, 1], delay }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Mobile How It Works - simple cycling animation
const MobileHowItWorks = ({ steps, navigate }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const videoRef = useRef(null);
  
  // Cycle through steps every 2 seconds (adjusted for 0.8x playback speed)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % 3);
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Slow down video playback by 20%
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.8;
    }
  }, []);

  return (
    <div className="md:hidden">
      {/* Steps container - reduced padding */}
      <AnimatedSection className="mb-4">
        <div className="bg-white rounded-2xl p-4 max-w-sm mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-4"
            >
              <div className="w-20 h-20 flex-shrink-0">
                <img 
                  src={steps[currentStep].numberImage} 
                  alt={`Step ${currentStep + 1}`}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-heading font-semibold text-slate-900">{steps[currentStep].title}</h3>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </AnimatedSection>

      {/* Video - slowed down by 20% */}
      <AnimatedSection>
        <div className="rounded-2xl overflow-hidden mx-auto max-w-sm" style={{ backgroundColor: '#F8F5F7' }}>
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-auto"
          >
            <source src="https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/a9o4jp25_openart-23474568-metadata_user_09546794dd135234_1788755267835_bf149e8e.mp4" type="video/mp4" />
          </video>
        </div>
      </AnimatedSection>

      {/* CTA for mobile */}
      <AnimatedSection className="text-center mt-8">
        <Button
          size="lg"
          className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-base font-medium transition-all duration-300 hover:scale-[1.02]"
          onClick={() => navigate('/how-it-works')}
        >
          See how it works <ArrowRight className="ml-2" size={18} weight="bold" />
        </Button>
      </AnimatedSection>
    </div>
  );
};

// Project Card Component for Carousel
const ProjectCard = ({ project, isHighlighted }) => {
  if (!project) return null;
  return (
    <div
      className={`h-full rounded-2xl transition-shadow duration-500 ${isHighlighted ? 'shadow-2xl shadow-primary/20' : 'shadow-lg'}`}
      data-testid={`project-card-${project.id}`}
    >
      <OpportunityCard project={project} />
    </div>
  );
};

// Project carousel - one continuous track, so cards glide instead of popping between slides
const CAROUSEL_EASE = [0.22, 0.61, 0.36, 1];
const CAROUSEL_TRANSITION = { duration: 0.55, ease: CAROUSEL_EASE };
// Every card occupies an identical slot; only the scale of the neighbours differs
const CARD_WIDTH = 380;
const CARD_GUTTER = 28;
const MOBILE_CARD_RATIO = 0.70;

const ProjectCarousel = ({ projects, navigate }) => {
  const count = projects.length;
  const loop = count > 1;
  // Three copies of the list, so there is always a card either side and the row never starts empty
  const ring = loop ? [...projects, ...projects, ...projects] : projects;
  const base = loop ? count : 0;

  const [position, setPosition] = useState(base);
  const [snap, setSnap] = useState(false);
  const [mobileCard, setMobileCard] = useState(0);
  // The desktop window is measured, so three whole cards always fit inside it - never half a card
  const windowRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(0);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);

  const activeIndex = loop ? ((position - base) % count + count) % count : 0;

  useEffect(() => {
    const measure = () => setMobileCard(Math.round(window.innerWidth * MOBILE_CARD_RATIO));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    const el = windowRef.current;
    if (!el) return;
    const measure = () => setWindowWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Re-enable the easing on the frame after a silent reset
  useEffect(() => {
    if (!snap) return;
    const id = requestAnimationFrame(() => setSnap(false));
    return () => cancelAnimationFrame(id);
  }, [snap]);

  const step = (delta) => setPosition((p) => p + delta);

  // Once a move lands on an outer copy, jump back to the middle one without animating
  const settle = () => {
    if (!loop) return;
    if (position >= base + count) {
      setSnap(true);
      setPosition(position - count);
    } else if (position < base) {
      setSnap(true);
      setPosition(position + count);
    }
  };

  const handleTouchEnd = () => {
    if (touchStartRef.current === null || touchEndRef.current === null) return;
    const distance = touchStartRef.current - touchEndRef.current;
    if (Math.abs(distance) > 30) step(distance > 0 ? 1 : -1);
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  // A click on a card that is not in focus brings it forward rather than opening it
  const selectOnly = (slot) => (e) => {
    if (slot === position) return;
    e.preventDefault();
    e.stopPropagation();
    setPosition(slot);
  };

  const transition = snap ? { duration: 0 } : CAROUSEL_TRANSITION;

  // Three across once there is room for three whole cards, otherwise one at a time
  const perView = windowWidth >= 900 ? 3 : 1;
  const desktopCard = windowWidth
    ? Math.min(
        CARD_WIDTH,
        perView === 3 ? Math.floor((windowWidth - 2 * CARD_GUTTER) / 3) : windowWidth - CARD_GUTTER,
      )
    : CARD_WIDTH;
  const desktopSlide = desktopCard + CARD_GUTTER;
  // With a single card in view the clip is exactly one card wide, so a neighbour is never half-shown
  const desktopViewport = perView === 3 ? windowWidth : desktopCard;
  const desktopLead = Math.max((desktopViewport - desktopCard) / 2, 0);

  const slides = (isDesktop) =>
    (isDesktop ? ring : projects).map((project, slot) => {
      const isActive = isDesktop ? slot === position : slot === activeIndex;
      return (
        <motion.div
          key={`${project.id || 'p'}-${slot}`}
          className="flex-shrink-0"
          style={{
            width: isDesktop ? desktopCard : mobileCard,
            marginRight: isDesktop ? CARD_GUTTER : 16,
          }}
          animate={{ scale: isActive ? 1 : isDesktop ? 0.88 : 0.93 }}
          transition={transition}
          onClickCapture={isDesktop ? selectOnly(slot) : undefined}
        >
          <ProjectCard project={project} navigate={navigate} isHighlighted={isActive} isDesktop={isDesktop} />
        </motion.div>
      );
    });

  const mobileSlide = mobileCard + 16;

  return (
    <>
      {/* Mobile / tablet - the active card is centred, neighbours peek evenly on both sides */}
      <div
        className="relative overflow-hidden md:hidden touch-pan-y -mx-6"
        onTouchStart={(e) => {
          touchStartRef.current = e.touches[0].clientX;
          touchEndRef.current = null;
        }}
        onTouchMove={(e) => {
          touchEndRef.current = e.touches[0].clientX;
        }}
        onTouchEnd={handleTouchEnd}
      >
        <motion.div
          className="flex items-stretch py-4"
          style={{ paddingLeft: `calc(50% - ${mobileCard / 2}px)` }}
          animate={{ x: -activeIndex * mobileSlide }}
          transition={CAROUSEL_TRANSITION}
          data-testid="carousel-track-mobile"
        >
          {slides(false)}
        </motion.div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-8 z-10 bg-gradient-to-r from-slate-900 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-8 z-10 bg-gradient-to-l from-slate-900 to-transparent" />
      </div>

      {/* Desktop - three cards at all times inside a fixed window, arrows in the gutters outside it */}
      <div className="hidden md:block relative px-12 lg:px-14">
        <div ref={windowRef}>
          <div
            className="overflow-hidden mx-auto"
            style={desktopViewport ? { maxWidth: desktopViewport } : undefined}
          >
          <motion.div
            className="flex items-stretch py-8"
            style={{ paddingLeft: desktopLead }}
            animate={{ x: -position * desktopSlide }}
            transition={transition}
            onAnimationComplete={settle}
            data-testid="carousel-track"
          >
            {slides(true)}
          </motion.div>
          </div>
        </div>

        {loop && (
          <>
            <button
              onClick={() => step(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300"
              data-testid="carousel-prev-btn"
              aria-label="Previous project"
            >
              <ArrowRight className="rotate-180 text-white" size={24} />
            </button>
            <button
              onClick={() => step(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300"
              data-testid="carousel-next-btn"
              aria-label="Next project"
            >
              <ArrowRight className="text-white" size={24} />
            </button>
          </>
        )}

        {loop && (
          <div className="flex items-center justify-center gap-2 mt-2">
            {projects.map((project, i) => (
              <button
                key={project.id || i}
                onClick={() => setPosition(base + i)}
                aria-label={`Go to project ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === activeIndex ? 'w-7 bg-white/80' : 'w-1.5 bg-white/25 hover:bg-white/40'
                }`}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// The Old Way vs Flippex Way Section - Simplified time-based animation
const ComparisonSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [showStrikes, setShowStrikes] = useState(false);
  
  // Check for reduced motion preference
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }
  }, []);
  
  // Trigger strikethrough animation after section is in view
  useEffect(() => {
    if (isInView && !prefersReducedMotion) {
      const timer = setTimeout(() => {
        setShowStrikes(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (prefersReducedMotion) {
      setShowStrikes(true);
    }
  }, [isInView, prefersReducedMotion]);

  const pillars = [
    {
      image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/opji06q3_08B62DFC-0CD2-464F-AEDB-527FA6BD4B85.png',
      title: '€100+ to get started',
      description: 'Own a fraction of an investment property.',
    },
    {
      image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/7vimbc3h_A1722664-49C7-4DF4-AA13-673756EA761F.png',
      title: 'Income + appreciation',
      description: 'Benefit from rental distributions and potential property appreciation.',
    },
    {
      image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/0tcms7r1_CB4BBD3C-75DF-440D-B310-A6FDD433F5EF.png',
      title: 'Professionally managed',
      description: 'We source and manage the property. You follow everything digitally.',
    },
  ];

  return (
    <section 
      ref={ref}
      className="py-20 md:py-27 bg-[rgb(var(--background))]"
      data-testid="comparison-section"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto">
          
          {/* Traditional section */}
          <div className="text-center mb-20">
            <p className="text-sm font-medium text-slate-400 tracking-[0.2em] uppercase mb-8">
              Traditional real estate investing
            </p>

            <div className="space-y-3 md:space-y-4">
              {/* Pain point 1 */}
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-500">
                <span className="relative inline-block">
                  €500,000 property purchase
                  <motion.span 
                    className="absolute left-0 top-1/2 h-[2px] md:h-[3px] bg-primary/70 -translate-y-1/2 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: showStrikes ? '100%' : '0%' }}
                    transition={{ duration: 0.5, delay: 0 }}
                  />
                </span>
              </h2>

              {/* Pain point 2 */}
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-500">
                <span className="relative inline-block">
                  Manage contractors
                  <motion.span 
                    className="absolute left-0 top-1/2 h-[2px] md:h-[3px] bg-primary/70 -translate-y-1/2 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: showStrikes ? '100%' : '0%' }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  />
                </span>
              </h2>

              {/* Pain point 3 */}
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-500">
                <span className="relative inline-block">
                  Manage tenants
                  <motion.span 
                    className="absolute left-0 top-1/2 h-[2px] md:h-[3px] bg-primary/70 -translate-y-1/2 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: showStrikes ? '100%' : '0%' }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  />
                </span>
              </h2>
            </div>
          </div>

          {/* With Flippex section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={showStrikes ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-sm font-medium text-slate-400 tracking-[0.2em] uppercase mb-6">
              With Flippex
            </p>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-primary max-w-3xl mx-auto mb-6">
              Own a share of curated real estate from just €100.
            </h2>
            
            <p className="text-lg md:text-xl text-foreground-muted max-w-2xl mx-auto">
              Flippex gives you access to professionally selected and managed investment properties — without buying an entire property yourself.
            </p>
          </motion.div>

        </div>
      </div>

      {/* Three pillars - full width cards like opportunities */}
      <motion.div 
        className="container mx-auto px-6 md:px-12 lg:px-20"
        initial={{ opacity: 0, y: 20 }}
        animate={showStrikes ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {pillars.map((pillar, index) => {
            return (
              <motion.div 
                key={index} 
                className="bg-white border border-black/[0.04] rounded-2xl p-4 md:p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={showStrikes ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.4, delay: 1.4 + (index * 0.15) }}
              >
                <div className="flex items-center gap-4 md:gap-5">
                  <div className="w-24 h-24 md:w-28 md:h-28 flex-shrink-0">
                    <img 
                      src={pillar.image} 
                      alt={pillar.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg md:text-xl font-heading font-medium text-slate-900">
                      {pillar.title}
                    </h3>
                    <p className="text-sm text-foreground-muted">
                      {pillar.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};

const HomePage = () => {
  const { openAuthModal } = useAuth();
  const navigate = useNavigate();
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const heroRef = useRef(null);
  const desktopVideoRef = useRef(null);
  
  // Parallax effect for hero
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  // Clamp opacity to stay at 0 after fading out (no reverse)
  const heroOpacity = useTransform(scrollYProgress, [0, 0.75, 0.95], [1, 1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 0.9], [0, 100]);
  
  // Slow down desktop video playback by 20%
  useEffect(() => {
    if (desktopVideoRef.current) {
      desktopVideoRef.current.playbackRate = 0.8;
    }
  }, []);

  // Fetch featured projects (stats are static)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // /api/projects already returns only publicly visible projects
        const projectsResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects?limit=50`);
        const sortedProjects = sortProjects(projectsResponse.data, 'publishedDate_desc');
        setFeaturedProjects(sortedProjects); // Show all projects, no slice
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchData();
  }, []);

  // How it works steps with custom 3D number images
  const steps = [
    { 
      numberImage: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/iq1l73xl_E2C0487F-582E-4548-97F3-4643134DA899.png',
      title: 'Find your project.', 
      description: 'Explore curated projects and understand the property, investment strategy, expected returns and associated risks.' 
    },
    { 
      numberImage: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/28tk868y_13DA273F-6918-461D-B6A7-D4463825E7D6.png',
      title: 'Own a fraction.', 
      description: 'Invest from €100 and receive the appropriate digital representation of your participation in the investment.' 
    },
    { 
      numberImage: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/wcp5vang_C5243FD9-B382-4322-A719-2A6070D4764A.png',
      title: 'Reap the earnings.', 
      description: 'Participate in the economics of the investment without managing the property yourself.' 
    },
  ];

  // FAQ items
  const faqs = [
    { q: 'How much do I need to start?', a: '€100 gets you through the door. Most projects start from this amount.' },
    { q: 'Do I need to manage the property?', a: 'No. You invest. We handle property management, tenants, and everything else.' },
    { q: 'How do I earn returns?', a: 'Participate in rental income and capital appreciation. Returns are distributed directly to your account.' },
    { q: 'Can I exit my investment?', a: 'Some projects offer liquidity through our marketplace. Terms vary by project.' },
  ];

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="home-page">
      <Header />

      {/* Hero Section - Extremely Simple */}
      <section 
        ref={heroRef}
        className="relative min-h-[100vh] flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image - different for mobile and desktop */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden md:block"
          style={{
            backgroundImage: 'url("https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/pc0vjq8p_9206EEC1-3815-486C-A230-C9B4A791144D.png")'
          }}
        />
        {/* Mobile hero image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat md:hidden"
          style={{
            backgroundImage: 'url("https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/f522zmyk_C9BDB258-9F2C-4B10-9CD4-9964F2BAA204.png")'
          }}
        />
        
        {/* Dark overlay - reduced for more picture visibility */}
        <div className="absolute inset-0 bg-slate-900/70" />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />

        <motion.div 
          style={{ opacity: heroOpacity, y: heroY }}
          className="container mx-auto px-6 md:px-12 lg:px-20 relative z-10"
        >
          <div className="max-w-5xl">
            {/* Provocative Proposition - Left aligned */}
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-2"
            >
              <span className="block text-5xl sm:text-6xl md:text-6xl lg:text-7xl font-heading font-medium text-primary tracking-tight">
                Own real estate.
              </span>
            </motion.h1>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mb-10"
            >
              <span className="block text-5xl sm:text-6xl md:text-6xl lg:text-7xl font-heading font-medium text-white tracking-tight">
                Without buying the whole thing.
              </span>
            </motion.h1>
            
            {/* Brand Promise */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-xl md:text-2xl text-white/70 mb-20 md:mb-24 font-light"
            >
              Welcome to a better way into real estate.
            </motion.p>
            
            {/* CTAs */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="flex flex-col sm:flex-row gap-4 mb-16"
            >
              <Button
                size="lg"
                className="bg-primary hover:bg-primary-hover text-white rounded-full px-10 py-7 text-lg font-medium transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/25"
                onClick={() => navigate('/projects')}
                data-testid="hero-cta-explore"
              >
                Explore projects
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-10 py-7 text-lg font-medium border-white/20 text-white hover:bg-white/5 transition-all duration-300"
                onClick={() => navigate('/how-it-works')}
              >
                How it works
              </Button>
            </motion.div>

            {/* KPI / Social Proof Strip - Static values matching About page */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 max-w-3xl"
            >
              <div data-testid="hero-stat-raised">
                <p className="text-3xl md:text-4xl font-heading font-semibold text-white">
                  <AnimatedCounter value="2.0M" prefix="€" suffix="+" duration={2000} immediate />
                </p>
                <p className="text-sm text-white/50 mt-1">Raised</p>
              </div>
              <div data-testid="hero-stat-returns">
                <p className="text-3xl md:text-4xl font-heading font-semibold text-white">
                  <AnimatedCounter value="250k" prefix="€" suffix="+" duration={2000} immediate />
                </p>
                <p className="text-sm text-white/50 mt-1">Paid in returns</p>
              </div>
              <div data-testid="hero-stat-minimum">
                <p className="text-3xl md:text-4xl font-heading font-semibold text-primary">
                  <AnimatedCounter value="100" prefix="€" duration={1500} immediate />
                </p>
                <p className="text-sm text-white/50 mt-1">Minimum investment</p>
              </div>
              <div data-testid="hero-stat-projects-funded">
                <p className="text-3xl md:text-4xl font-heading font-semibold text-white">
                  <AnimatedCounter value="5" duration={1500} immediate />
                </p>
                <p className="text-sm text-white/50 mt-1">Projects funded</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* External validation, straight after the hero */}
      <RecognitionStrip />

      {/* The Old Way vs Flippex Way - Scroll-driven Section */}
      <ComparisonSection />

      {/* How It Works Section */}
      <section className="py-24 md:py-30 bg-white" data-testid="how-it-works-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          {/* Mobile title - centered */}
          <AnimatedSection className="text-center mb-12 md:hidden">
            <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-6">
              How It Works
            </p>
            <h2 className="text-4xl font-heading font-medium tracking-tight mb-4 text-slate-900">
              Being a landlord.
            </h2>
            <h2 className="text-4xl font-heading font-medium tracking-tight text-slate-400">
              The easy, smart and digital way.
            </h2>
          </AnimatedSection>

          {/* Desktop Layout: Video left, Title + Steps + CTA right */}
          <div className="hidden md:grid md:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Video - fits screen height without cropping, slowed down by 20% */}
            <AnimatedSection className="relative sticky top-24">
              <div className="rounded-3xl overflow-hidden" style={{ maxHeight: 'calc(100vh - 120px)', backgroundColor: '#FCFEFB' }}>
                <video
                  ref={desktopVideoRef}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-contain"
                  style={{ maxHeight: 'calc(100vh - 120px)' }}
                >
                  <source src="https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/a9o4jp25_openart-23474568-metadata_user_09546794dd135234_1788755267835_bf149e8e.mp4" type="video/mp4" />
                </video>
              </div>
            </AnimatedSection>

            {/* Title + Steps + CTA */}
            <AnimatedSection delay={0.2}>
              {/* Desktop title - in right column */}
              <div className="mb-10">
                <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-4">
                  How It Works
                </p>
                <h2 className="text-4xl lg:text-5xl font-heading font-medium tracking-tight mb-2 text-slate-900">
                  Being a landlord.
                </h2>
                <h2 className="text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-400">
                  The easy, smart and digital way.
                </h2>
              </div>

              <div className="space-y-8">
                {steps.map((step, index) => (
                  <div key={index} className="flex items-center gap-5">
                    <div className="w-20 h-20 lg:w-24 lg:h-24 flex-shrink-0">
                      <img 
                        src={step.numberImage} 
                        alt={`Step ${index + 1}`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg lg:text-xl font-heading font-medium mb-1">{step.title}</h3>
                      <p className="text-sm text-foreground-muted leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* CTA under steps on desktop */}
              <div className="mt-10">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-base font-medium transition-all duration-300 hover:scale-[1.02]"
                  onClick={() => navigate('/how-it-works')}
                >
                  See how it works <ArrowRight className="ml-2" size={18} weight="bold" />
                </Button>
              </div>
            </AnimatedSection>
          </div>

          {/* Mobile Layout: Steps container with scroll-lock animation, then video */}
          <MobileHowItWorks steps={steps} navigate={navigate} />
        </div>
      </section>

      {/* Featured Projects Section - Endless Carousel */}
      <section className="py-24 md:py-30 bg-slate-900 overflow-hidden" data-testid="featured-projects-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <AnimatedSection className="text-center mb-16">
            <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-6">
              Projects
            </p>
            <h2 className="text-4xl lg:text-5xl font-heading font-medium tracking-tight mb-4 text-white">
              So, what would you like to own?
            </h2>
          </AnimatedSection>

          {loadingProjects ? (
            <div className="flex justify-center py-20">
              <FlippexLoader size={48} animate={false} />
            </div>
          ) : featuredProjects.length > 0 ? (
            <ProjectCarousel projects={featuredProjects} navigate={navigate} />
          ) : (
            <div className="text-center py-20 text-white/60">
              <Buildings size={64} className="mx-auto mb-6 opacity-30" />
              <p className="text-xl">No projects available at the moment.</p>
              <p className="text-white/40 mt-2">Check back soon!</p>
            </div>
          )}

          <AnimatedSection delay={0.3} className="text-center mt-12">
            <Button
              variant="outline"
              className="rounded-full px-8 py-6 text-base border-white/30 text-white hover:bg-white/10 transition-all duration-300"
              onClick={() => navigate('/projects')}
              data-testid="view-all-projects-btn"
            >
              Find something worth owning <ArrowRight className="ml-2" size={18} />
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Trust Section - Beautiful Safe with animated shield */}
      <section className="py-24 md:py-30 bg-[rgb(var(--background))]">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <AnimatedSection className="text-center mb-8">
            <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-6">
              Trust & Transparency
            </p>
            <h2 className="text-4xl lg:text-5xl font-heading font-medium tracking-tight mb-4 text-slate-900">
              Beautifully safe.
            </h2>
          </AnimatedSection>

          {/* Shield with gentle Y-axis oscillation - visible on all screens */}
          <div className="flex justify-center mb-11" style={{ perspective: '1000px' }}>
            <motion.img 
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/fuuxbgtt_9D1605CB-E5BE-4C94-BDA3-9794815D6139.png"
              alt="Trust Shield"
              className="w-[60%] max-w-[400px] h-auto object-contain"
              animate={{ 
                rotateY: [-10, 10, -10]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>

          {/* Desktop: 6-column grid (3-column for tablet) */}
          <div className="hidden md:block">
            <AnimatedSection delay={0.2}>
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
                {[
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/s76pjo4s_4629EB97-D394-49AD-B644-12759AD724EA.png', title: 'Professional Due Diligence', desc: 'Every project is vetted' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/yvsjd8c8_BD795818-5A3D-4984-8D98-92E43EA0A157.png', title: 'Full Documentation', desc: 'Legal and financial transparency' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/b1nf2ri8_33989EFD-D6CC-4CA2-B2FE-2A39E29B4952.png', title: 'Clear Fee Structure', desc: 'No hidden costs' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/wqqop72z_E3957D44-A8F7-469C-9D59-92388896B403.png', title: 'Backed by Real Assets', desc: 'Cash tied to property' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/gxdx0cym_548A448D-8FAD-42A6-9DDF-248BF3CC220B.png', title: 'Security', desc: 'Tokenized security of real asset cashflows' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/wofp6q76_6BBADFB7-B06C-4F67-AE32-6A95BA0E87D0.png', title: 'Compliance', desc: 'Licensed and audited infrastructure' },
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-2xl p-4 lg:p-6 text-center shadow-sm">
                    <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-3 lg:mb-4">
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <p className="font-semibold mb-1 text-xs lg:text-sm">{item.title}</p>
                    <p className="text-[10px] lg:text-xs text-foreground-muted hidden lg:block">{item.desc}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>

          {/* Mobile: 2-column grid */}
          <div className="md:hidden">

            <AnimatedSection delay={0.2}>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/s76pjo4s_4629EB97-D394-49AD-B644-12759AD724EA.png', title: 'Professional Due Diligence', desc: 'Every project is vetted' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/yvsjd8c8_BD795818-5A3D-4984-8D98-92E43EA0A157.png', title: 'Full Documentation', desc: 'Legal and financial transparency' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/b1nf2ri8_33989EFD-D6CC-4CA2-B2FE-2A39E29B4952.png', title: 'Clear Fee Structure', desc: 'No hidden costs' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/wqqop72z_E3957D44-A8F7-469C-9D59-92388896B403.png', title: 'Backed by Real Assets', desc: 'Cash tied to property' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/gxdx0cym_548A448D-8FAD-42A6-9DDF-248BF3CC220B.png', title: 'Security', desc: 'Tokenized security of real asset cashflows' },
                  { image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/wofp6q76_6BBADFB7-B06C-4F67-AE32-6A95BA0E87D0.png', title: 'Compliance', desc: 'Licensed and audited infrastructure' },
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                    <div className="w-20 h-20 mx-auto mb-4">
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <p className="font-semibold mb-1 text-sm">{item.title}</p>
                    <p className="text-xs text-foreground-muted">{item.desc}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-24 md:py-30 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <AnimatedSection>
              <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-6">
                Technology
              </p>
              <h2 className="text-4xl lg:text-5xl font-heading font-medium tracking-tight mb-4 leading-[1.1] text-slate-900">
                Rocket science underneath.
              </h2>
              <h2 className="text-4xl lg:text-5xl font-heading font-medium tracking-tight mb-8 text-slate-400 leading-[1.1]">
                Simple on top.
              </h2>
              {/* Subtitle only on mobile */}
              <p className="text-xl text-foreground-muted leading-relaxed font-light md:hidden">
                You get a seamless investment experience. We handle the complexity behind the scenes.
              </p>
              
              {/* Regulatory text and badge - Desktop only */}
              <div className="hidden md:block mt-10 pt-8 border-t border-black/[0.06]">
                <h3 className="text-lg font-heading font-medium text-primary mb-3">
                  Regulated infrastructure. Real assets.
                </h3>
                <p className="text-sm text-foreground-muted leading-relaxed mb-2">
                  Flippex combines MiFID II-compliant infrastructure through an authorized partner with a tokenized securities framework overseen by Germany's Federal Financial Supervisory Authority (BaFin).
                </p>
                <p className="text-sm text-foreground-muted leading-relaxed mb-4">
                  Each investment is backed by real estate directly owned and operated within the Flippex structure, connecting digital ownership to tangible, real-world assets.
                </p>
                <img 
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/cy7wi7xa_5F55C0B4-1D27-4A21-890B-D47A7D002634.webp"
                  alt="MiFID II Compliant"
                  className="h-20 w-auto object-contain"
                />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              {/* Subtitle on desktop - above the checks box */}
              <p className="text-xl text-foreground-muted leading-relaxed font-light mb-6 hidden md:block">
                You get a seamless investment experience. We handle the complexity behind the scenes.
              </p>
              <div className="bg-[rgb(var(--background))] rounded-3xl p-8 lg:p-10 border border-black/[0.04]">
                <div className="space-y-6">
                  {[
                    { label: 'Digital Ownership', desc: 'Track your participation instantly' },
                    { label: 'Automated Distributions', desc: 'Returns sent directly to you' },
                    { label: 'Transparent Reporting', desc: 'Real-time performance data' },
                    { label: 'Secure Infrastructure', desc: 'Bank-grade security standards' },
                    { label: 'European-Regulated', desc: 'MiFID II compliant infrastructure' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="text-primary" size={20} weight="fill" />
                      </div>
                      <div>
                        <p className="font-semibold">{item.label}</p>
                        <p className="text-sm text-foreground-muted">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
          
          {/* Regulatory text and badge - Mobile only, after all elements */}
          <div className="md:hidden mt-12 pt-8 border-t border-black/[0.06]">
            <h3 className="text-lg font-heading font-medium text-primary mb-3">
              Regulated infrastructure. Real assets.
            </h3>
            <p className="text-sm text-foreground-muted leading-relaxed mb-2">
              Flippex combines MiFID II-compliant infrastructure through an authorized partner with a tokenized securities framework overseen by Germany's Federal Financial Supervisory Authority (BaFin).
            </p>
            <p className="text-sm text-foreground-muted leading-relaxed mb-4">
              Each investment is backed by real estate directly owned and operated within the Flippex structure, connecting digital ownership to tangible, real-world assets.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
              <img 
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/cy7wi7xa_5F55C0B4-1D27-4A21-890B-D47A7D002634.webp"
                alt="MiFID II Compliant"
                style={{ height: '64px', width: 'auto' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* For Developers Section */}
      <ForDevelopersSection />

      {/* FAQ Section */}
      <section className="py-24 md:py-30 bg-[rgb(var(--background))]" data-testid="faq-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <AnimatedSection className="text-center mb-16">
            <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-6">
              Questions
            </p>
            <h2 className="text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900">
              Common questions.
            </h2>
          </AnimatedSection>

          <AnimatedSection delay={0.2} className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {faqs.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 border border-black/[0.04]">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Question className="text-primary" size={20} weight="bold" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">{item.q}</h4>
                      <p className="text-foreground-muted">{item.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button
                variant="outline"
                className="rounded-full px-8 py-6 border-primary/30 text-primary hover:bg-primary/5"
                onClick={() => navigate('/faqs')}
              >
                View All FAQs <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA */}
      <CTASection />

      <Footer />
    </div>
  );
};

export default HomePage;
